import { FC, useEffect, useState } from 'react';
import { Chessboard } from 'react-chessboard';
import { Button, Divider, Image, Modal, Tabs } from 'antd';
import LichessPgnViewer from 'lichess-pgn-viewer';
import { EditOutlined, QuestionOutlined } from '@ant-design/icons';

import {
  api,
  filteredLocalizations,
  IEnumCustomProblemType,
  IEnumExerciseGamePhase,
  IEnumExerciseTarget,
  IEnumLocalizationGenitive,
  IExercise,
  ILocalization,
} from '@chess-class/sandbox';
import { ExerciseCustomProblem } from '@chess-class/sandbox/components';

import BaseSkeleton from '~/components/atoms/BaseSkeleton';
import { ChessPgnViewer } from '~/components/atoms/ChessPgnViewer';
import { FaChessKnightIcon } from '~/components/atoms/icons';
import { NotFoundResult } from '~/components/atoms/NotFoundResult';
import { TitleAndText } from '~/components/atoms/TitleAndText';
import { useEducationFormat } from '~/context/EducationFormContext';
import { checkEditPermission } from '~/utils/checkEditPermission';

import { QuestionAnswersCard } from '~mMaterials/exercises/exercises/components/molecules/QuestionAnswersCard';
import { userPlayLevels } from '~mMaterials/exercises/exercises/types/UserPlayLevel';

const EXERCISE_VIEW_TABS = {
  EXERCISE_DETAILS: 'EXERCISE_DETAILS',
  EXERCISE_PGN_VIEWER: 'EXERCISE_PGN_VIEWER',
};

const pgnViewerId = 'exercies-desk';

export const ExerciseViewModal: FC<{
  id?: string;
  isOpen: boolean;
  onCancel: () => void;
  openEdit?: () => void;
}> = ({ id = '', isOpen, onCancel, openEdit }) => {
  const exerciseQuery = api.content.exercises.useExercise({ id });
  const exercise = exerciseQuery.data?.data;
  const [activeTab, setActiveTab] = useState(EXERCISE_VIEW_TABS.EXERCISE_DETAILS);
  const canEditExercises = checkEditPermission('materials');
  const { defaultEducationFormat } = useEducationFormat();

  const configureGameHistory = (exercise: IExercise, element: Element | null) => {
    if (element && exercise) {
      LichessPgnViewer(element as HTMLElement, {
        fen: exercise.problem?.boardState,
        pgn: exercise.problem?.moveSequence,
        showControls: false,
        showPlayers: false,
      });
    }
  };

  useEffect(() => {
    const element = document.querySelector(`#${pgnViewerId}`);
    if (exercise && activeTab === EXERCISE_VIEW_TABS.EXERCISE_PGN_VIEWER && element) {
      configureGameHistory(exercise, element);
    }
  }, [exercise, activeTab]);

  return (
    <Modal
      className="w-full px-2 md:px-0 md:w-4/5 top-10"
      footer={null}
      onCancel={onCancel}
      open={isOpen}
      title={
        exercise?.type && (
          <div className="flex flex-row gap-4 truncate">
            <h1 className="text-xl m-0 flex items-center gap-1">
              {exercise.type == 'TEST' ? (
                <>
                  <QuestionOutlined className="text-ant-orange-6" /> Тест #{id}
                </>
              ) : (
                <>
                  <FaChessKnightIcon /> Упражнение #{id}
                </>
              )}
            </h1>

            {canEditExercises && openEdit && (
              <>
                <Button
                  className="flex items-center justify-center"
                  icon={<EditOutlined />}
                  onClick={openEdit}
                >
                  <span className="hidden md:block">Редактировать</span>
                </Button>
              </>
            )}
          </div>
        )
      }
    >
      <Tabs
        activeKey={activeTab}
        defaultActiveKey="2"
        items={[
          {
            children: exerciseQuery.isLoading ? (
              <BaseSkeleton />
            ) : !exercise ? (
              <NotFoundResult title="Упражнение не найдено" />
            ) : (
              <div className="flex flex-col lg:flex-row gap-4">
                <div className="-mr-4 sm:mx-0 sm:p-4 flex flex-col gap-4 md:min-w-[350px]">
                  <TitleAndText
                    light
                    text={
                      exercise.level ? (
                        <p className={'mb-0'} key={exercise.level}>
                          {userPlayLevels[exercise.level]}
                        </p>
                      ) : (
                        '-'
                      )
                    }
                    title="Уровень"
                  />

                  <TitleAndText
                    light
                    text={IEnumExerciseGamePhase[exercise.gamePhase || '']}
                    title="Стадия игры"
                  />

                  <TitleAndText light text={exercise.isTactic ? 'Да' : 'Нет'} title="Тактика" />

                  <TitleAndText
                    light
                    text={exercise.isCapitalization ? 'Да' : 'Нет'}
                    title="Капитализация преимущества"
                  />

                  <TitleAndText
                    light
                    text={exercise.chapter?.translations?.ru?.title}
                    title="Тема"
                  />

                  <TitleAndText
                    light
                    text={IEnumExerciseTarget[exercise.target]}
                    title="Место для применения"
                  />

                  {exercise.type === 'PROBLEM' && (
                    <TitleAndText
                      light
                      text={exercise.problem?.boardState ? exercise.problem?.boardState : '-'}
                      title="FEN"
                    />
                  )}

                  {exercise.type === 'CUSTOM_PROBLEM' && (
                    <>
                      <Divider />
                      <h3 className="text-xl font-bold">Особые детали</h3>
                      <TitleAndText
                        light
                        text={IEnumCustomProblemType[exercise.customProblem?.type || 'FIGURE']}
                        title="Тип"
                      />
                      <TitleAndText
                        light
                        text={exercise.customProblem?.boardState || '-'}
                        title="FEN"
                      />
                      <TitleAndText
                        light
                        text={exercise.customProblem?.moveCount || '-'}
                        title="Количество ходов"
                      />
                      <TitleAndText
                        light
                        text={exercise.customProblem?.opponentCanTake ? 'Да' : 'Нет'}
                        title="Соперник съедает"
                      />
                    </>
                  )}

                  {exercise.test?.imageUrl && (
                    <Image
                      className="mt-2 max-h-72 pr-4 rounded-md text-center object-contain"
                      fallback="data:image/png;base64,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"
                      preview={false}
                      src={exercise.test.imageUrl}
                    />
                  )}

                  {exercise.test?.answerImageUrl && (
                    <Image
                      className="mt-2 max-h-72 pr-4 rounded-md text-center object-contain"
                      fallback="data:image/png;base64,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"
                      preview={false}
                      src={exercise.test.answerImageUrl}
                    />
                  )}
                </div>

                {exercise.type == 'TEST' ? (
                  <div className="flex flex-col gap-6">
                    {filteredLocalizations({
                      defaultEducationFormat: defaultEducationFormat ?? 'GENERAL',
                      isDev:
                        process.env.REACT_APP_API_URL.includes('api.dev') ||
                        process.env.NODE_ENV == 'development',
                    })
                      ?.reverse()
                      .map((locale) => (
                        <QuestionAnswersCard
                          key={`question-answers-${locale}`}
                          localization={locale as ILocalization}
                          test={exercise.test}
                          text={`Вопрос на ${IEnumLocalizationGenitive[locale]}`}
                        />
                      ))}
                  </div>
                ) : exercise.type == 'PROBLEM' ? (
                  <div className="text-center flex flex-col" style={{ maxWidth: 360 }}>
                    <div className="shadow-md rounded-sm overflow-x-auto">
                      <Chessboard
                        arePiecesDraggable={false}
                        boardWidth={360}
                        position={exercise.problem?.boardState || ''}
                      />
                    </div>

                    <p className="mt-2 mb-0">{exercise.problem?.moveSequence}</p>
                  </div>
                ) : null}
              </div>
            ),
            key: EXERCISE_VIEW_TABS.EXERCISE_DETAILS,
            label: `Детали`,
          },
          ...(exercise?.type === 'PROBLEM' || exercise?.type == 'CUSTOM_PROBLEM'
            ? [
                {
                  children:
                    exercise.type == 'CUSTOM_PROBLEM' && exercise.customProblem ? (
                      <ExerciseCustomProblem
                        customProblem={exercise.customProblem}
                        sidebar={{ show: true, tryAgain: true }}
                      />
                    ) : (
                      exercise?.type == 'PROBLEM' && <ChessPgnViewer id={pgnViewerId} />
                    ),
                  forceRender: true,
                  key: EXERCISE_VIEW_TABS.EXERCISE_PGN_VIEWER,
                  label: exercise.type == 'CUSTOM_PROBLEM' ? `Особая доска` : 'Просмотр ходов',
                },
              ]
            : []),
        ]}
        onChange={(active) => setActiveTab(active)}
      />
    </Modal>
  );
};
